/**
 * Per-session BATON reconnect telemetry — caps log spam and flags reconnect storms for fleet.
 */
import { diagnosticLog } from '../utils/diagnosticLogger';

export const BATON_RECONNECT_STORM_THRESHOLD = 40;
export const BATON_WS_LOG_CAP_PER_SESSION = 8;
export const BATON_RECONNECT_STORM_MARKER = 'baton_reconnect_storm_v1';

let errorCount = 0;
let closeCount = 0;
let code1006Count = 0;
let suppressedLogCount = 0;
let stormReported = false;
let logsEmitted = 0;
let summarySent = false;

export function resetBatonReconnectSessionTrackerForTests(): void {
  errorCount = 0;
  closeCount = 0;
  code1006Count = 0;
  suppressedLogCount = 0;
  stormReported = false;
  logsEmitted = 0;
  summarySent = false;
}

export function getBatonReconnectSessionStats(): {
  errorCount: number;
  closeCount: number;
  code1006Count: number;
  suppressedLogCount: number;
  stormReported: boolean;
  totalEvents: number;
} {
  return {
    errorCount,
    closeCount,
    code1006Count,
    suppressedLogCount,
    stormReported,
    totalEvents: errorCount + closeCount,
  };
}

function maybeReportStorm(): void {
  const total = errorCount + closeCount;
  if (stormReported || total < BATON_RECONNECT_STORM_THRESHOLD) return;
  stormReported = true;
  diagnosticLog('warn', 'protocol', 'baton_reconnect_storm', {
    error_count: errorCount,
    close_count: closeCount,
    code_1006_count: code1006Count,
    suppressed_logs: suppressedLogCount,
    threshold: BATON_RECONNECT_STORM_THRESHOLD,
    marker: BATON_RECONNECT_STORM_MARKER,
  });
}

/** Returns true when the diagnostic line was emitted (false when capped). */
export function recordBatonWebSocketDiagnostic(
  kind: 'error' | 'close',
  message: string,
  payload: Record<string, unknown>,
): boolean {
  if (kind === 'error') {
    errorCount += 1;
  } else {
    closeCount += 1;
    if (payload.code === 1006) code1006Count += 1;
  }
  maybeReportStorm();

  if (logsEmitted >= BATON_WS_LOG_CAP_PER_SESSION) {
    suppressedLogCount += 1;
    return false;
  }
  logsEmitted += 1;
  diagnosticLog('warn', 'protocol', message, payload);
  return true;
}

/** Flush session summary on tab close when churn was elevated but below storm threshold. */
export function maybeFlushBatonReconnectSessionSummary(): void {
  if (summarySent) return;
  const total = errorCount + closeCount;
  if (total < 12) return;
  summarySent = true;
  diagnosticLog('info', 'protocol', 'baton_reconnect_session_summary', {
    error_count: errorCount,
    close_count: closeCount,
    code_1006_count: code1006Count,
    suppressed_logs: suppressedLogCount,
    storm_reported: stormReported,
    marker: BATON_RECONNECT_STORM_MARKER,
  });
}

if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener('pagehide', () => {
    maybeFlushBatonReconnectSessionSummary();
  });
}
